import { useCallback, useEffect, useRef } from 'react';

import { Box, CircularProgress, Typography, makeStyles, Theme, createStyles } from '@material-ui/core';

import { ChatData } from '../../types';
import { ChatListItem } from './ChatListItem';
import { ChatListSkeleton } from './kit';

interface Props {
  selectedChatId: string | null;
  chats: ChatData[];
  loading: boolean;
  onChatClick: (chat: ChatData) => void;
  hasMoreChats?: boolean;
  loadingMoreChats?: boolean;
  onLoadMore?: () => void;
  isLegend?: boolean;
  footer?: React.ReactNode;
  // Shown when there are no chats to list.
  emptyText?: string;
}

export const ChatList: React.FC<Props> = ({
  selectedChatId,
  chats,
  loading,
  onChatClick,
  hasMoreChats = false,
  loadingMoreChats = false,
  onLoadMore,
  isLegend = false,
  footer,
  emptyText = 'No chats yet',
}) => {
  const classes = useStyles();
  const observerRef = useRef<IntersectionObserver | null>(null);

  // The last row coming into view asks for the next page.
  const lastChatElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (loading || loadingMoreChats) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasMoreChats && onLoadMore) {
          onLoadMore();
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [loading, loadingMoreChats, hasMoreChats, onLoadMore]
  );

  useEffect(() => () => observerRef.current?.disconnect(), []);

  if (loading) {
    return (
      <Box className={classes.root}>
        <ChatListSkeleton />
      </Box>
    );
  }

  return (
    <Box className={classes.root} role="list" aria-label="Chats">
      {chats.map((chat, index) => (
        <div key={chat.id} role="listitem" ref={index === chats.length - 1 ? lastChatElementRef : null}>
          <ChatListItem
            chat={chat}
            onChatClick={onChatClick}
            selected={selectedChatId === chat.id}
            isLegend={isLegend}
          />
        </div>
      ))}

      {!chats.length && (
        <Typography color="textSecondary" align="center" className={classes.empty}>
          {emptyText}
        </Typography>
      )}

      {footer}

      {loadingMoreChats && (
        <Box display="flex" justifyContent="center" padding="16px">
          <CircularProgress size={24} />
        </Box>
      )}
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: { height: '100%', overflowY: 'auto', padding: '4px 8px 8px' },
    empty: { padding: '48px 24px', fontSize: 14 },
  })
);
